import { useCallback, useEffect, useMemo, useState } from "react";
import centrosService from "../../../../services/centrosService";
import cursosService from "../../../../services/cursosService";
import promocionesService from "../../../../services/promocionesService";
import dashboardAdminService from "../services/dashboardAdminService";

const initialFilters = {
  centro_id: "",
  curso_id: "",
  promocion_id: "",
  startDate: "",
  endDate: "",
};

export default function useAdminDashboard() {
  const [filters, setFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [centros, setCentros] = useState([]);
  const [cursos, setCursos] = useState([]);
  const [promociones, setPromociones] = useState([]);

  const loadCentros = useCallback(async () => {
    try {
      const list = await centrosService.listar();
      setCentros(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("No se pudieron cargar centros", err);
    }
  }, []);

  const loadCursos = useCallback(async (centroId) => {
    try {
      const list = centroId ? await cursosService.listarPorCentro(centroId) : await cursosService.listar();
      setCursos(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("No se pudieron cargar cursos", err);
    }
  }, []);

  const loadPromociones = useCallback(async (cursoId) => {
    try {
      const list = cursoId ? await promocionesService.listarPorCurso(cursoId) : await promocionesService.listar();
      setPromociones(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("No se pudieron cargar promociones", err);
    }
  }, []);

  const fetchDashboard = useCallback(
    async (customFilters) => {
      setLoading(true);
      setError(null);
      try {
        const payload = await dashboardAdminService.fetch(customFilters || filters);
        setData(payload);
      } catch (err) {
        console.error("Error cargando dashboard admin", err);
        setError(err?.message || "No se pudo cargar el dashboard");
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  // Load options on mount
  useEffect(() => {
    loadCentros();
  }, [loadCentros]);

  // Cascading loads
  useEffect(() => {
    loadCursos(filters.centro_id);
    // reset curso/promocion on centro change
    setFilters((prev) => ({
      ...prev,
      curso_id: "",
      promocion_id: "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.centro_id]);

  useEffect(() => {
    loadPromociones(filters.curso_id);
    setFilters((prev) => ({
      ...prev,
      promocion_id: "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.curso_id]);

  const handleChange = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const summary = useMemo(
    () => ({
      lastUpdate: data?.lastUpdate || null,
      kpis: data?.kpis || [],
    }),
    [data]
  );

  return {
    filters,
    data,
    summary,
    loading,
    error,
    centros,
    cursos,
    promociones,
    handleChange,
    fetchDashboard,
    resetFilters: () => setFilters(initialFilters),
  };
}
